import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { webLogger } from "../logging/logger";

const rawBaseQuery = fetchBaseQuery({ baseUrl: "/api" });

function elapsedMs(start: number): number {
  return Math.round(performance.now() - start);
}

function methodAndPath(args: string | FetchArgs): {
  method: string;
  path: string;
} {
  if (typeof args === "string") return { method: "GET", path: `/api${args}` };
  return {
    method: args.method ?? "GET",
    path: `/api${args.url}`,
  };
}

function requestIdFromMeta(meta: unknown): string | undefined {
  if (!meta || typeof meta !== "object" || !("response" in meta))
    return undefined;
  const response = (meta as { response?: Response }).response;
  return response?.headers.get("x-request-id") ?? undefined;
}

function serverMessage(error: FetchBaseQueryError): string | undefined {
  const data = error.data;
  if (typeof data === "string") return data.slice(0, 200);
  if (!data || typeof data !== "object" || !("message" in data))
    return undefined;

  const message = (data as { message?: unknown }).message;
  return typeof message === "string" ? message.slice(0, 200) : undefined;
}

export const baseQueryWithAuthHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const startedAt = performance.now();
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error) {
    const { method, path } = methodAndPath(args);
    const status = result.error.status;
    const log =
      status === 401 || status === 403 || status === 404
        ? webLogger.warn
        : webLogger.error;
    const message = serverMessage(result.error);
    const page = window.location.pathname;

    log({
      requestId: requestIdFromMeta(result.meta),
      httpMethod: method,
      path,
      handler: String(api.endpoint),
      statusCode: status,
      durationMs: elapsedMs(startedAt),
      message: `API request failed for ${api.endpoint} on page ${page}.${message ? ` Server message: ${message}.` : ""}`,
    });
  }

  if (result.error?.status === 401) {
    window.location.assign("/api/auth/login");
  }

  return result;
};
